import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';

import { AuditLogExplorerService } from '../../services/auditLogExplorer.service';
import { AuditLogExplorerTypes } from '../../types/auditLogExplorer.types';
import { ALE } from './tokens';

type Option = AuditLogExplorerTypes.OrganizationOption;

const labelSx = {
    fontSize: '10.5px',
    fontWeight: 600,
    letterSpacing: '.4px',
    textTransform: 'uppercase' as const,
    color: ALE.tFaint,
};

/**
 * Organization name → subscriptionId chip picker. Searches customers by name
 * (debounced) and emits the selected `subscriptionId[]` upward. Resolved
 * sub→name pairs are reported via `onResolveNames` so applied pills can show the
 * org name rather than a bare ObjectId.
 */
export const OrganizationLookup: React.FC<{
    value: string[]; // subscriptionIds
    onChange: (subs: string[]) => void;
    onResolveNames?: (map: Record<string, string>) => void;
}> = ({ value, onChange, onResolveNames }) => {
    const [input, setInput] = useState('');
    const [options, setOptions] = useState<Option[]>([]);
    const [loading, setLoading] = useState(false);
    // Remembers sub→name for everything we've ever seen, so selected chips keep
    // their label even after the search results that produced them change.
    const knownRef = useRef<Record<string, string>>({});
    // Bumped whenever knownRef gains new names, so the selectedOptions memo
    // recomputes and a chip showing a bare ObjectId picks up its resolved name.
    const [resolvedTick, setResolvedTick] = useState(0);

    // Hold the latest callback in a ref so the search effect doesn't depend on
    // its identity. The parent passes a fresh inline function each render, and
    // resolving names triggers a parent re-render — depending on it here would
    // make every result re-fire the search in an infinite loop.
    const resolveNamesRef = useRef(onResolveNames);
    useEffect(() => { resolveNamesRef.current = onResolveNames; });

    useEffect(() => {
        const q = input.trim();
        if (q.length < 2) {
            setOptions([]);
            setLoading(false);
            return;
        }
        let active = true;
        setLoading(true);
        const handle = setTimeout(async () => {
            const results = await AuditLogExplorerService.searchOrganizations(q);
            if (!active) return;
            results.forEach((r) => { knownRef.current[r.subscriptionId] = r.name; });
            resolveNamesRef.current?.({ ...knownRef.current });
            setResolvedTick((t) => t + 1);
            setOptions(results);
            setLoading(false);
        }, 300);
        return () => {
            active = false;
            clearTimeout(handle);
        };
    }, [input]);

    const selectedOptions = useMemo<Option[]>(
        () => value.map((sub) => ({ subscriptionId: sub, name: knownRef.current[sub] || sub })),
        // resolvedTick forces recompute when a late name lands in knownRef.
        [value, resolvedTick],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 260 }}>
            <Typography sx={labelSx}>Organization</Typography>
            <Autocomplete<Option, true, false, false>
                multiple
                size="small"
                filterSelectedOptions
                options={options}
                value={selectedOptions}
                loading={loading}
                inputValue={input}
                onInputChange={(_, v) => setInput(v)}
                onChange={(_, next) => onChange(next.map((o) => o.subscriptionId))}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a.subscriptionId === b.subscriptionId}
                noOptionsText={input.trim().length < 2 ? 'Type a name…' : 'No organizations match'}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                        const { key, ...chipProps } = getTagProps({ index });
                        return (
                            <Chip
                                key={key}
                                {...chipProps}
                                size="small"
                                label={option.name}
                                sx={{
                                    height: '22px',
                                    fontSize: '12px',
                                    bgcolor: ALE.g200,
                                    color: ALE.tSecondary,
                                    border: `1px solid ${ALE.g300}`,
                                }}
                            />
                        );
                    })
                }
                renderOption={(props, option) => {
                    const { key, ...optProps } = props as any;
                    return (
                        <li key={key} {...optProps} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: ALE.tPrimary }}>{option.name}</span>
                            <span style={{ fontSize: 11, color: ALE.tFaint, fontFamily: ALE.mono }}>sub: {option.subscriptionId}</span>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={value.length ? '' : 'Search organizations by name…'}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={14} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        sx={{
                            '& .MuiInputBase-root': {
                                fontSize: '13px',
                                borderRadius: '7px',
                                minHeight: '36px',
                                padding: '2px 8px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: ALE.g300 },
                        }}
                    />
                )}
            />
        </Box>
    );
};
